import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWrapper } from '../components/AppWrapper';
import { KpiCard } from '../components/KpiCard';
import { Card } from '../components/Card';
import { TimeSeriesChart } from '../components/TimeSeriesChart';
import { SystemHealthCard } from '../components/SystemHealthCard';
import { RecentCallsTable } from '../components/RecentCallsTable';
import { RecentToolLogsTable } from '../components/RecentToolLogsTable';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import type { Period, Interval } from '../hooks/useDashboardMetrics';

const PERIOD_VALUES: Period[] = ['today', '7days', '30days', 'this_month', 'last_month'];

const periodLabelKeys: Record<Period, string> = {
  today: 'dashboard.today',
  '7days': 'dashboard.last_7_days',
  '30days': 'dashboard.last_30_days',
  this_month: 'dashboard.this_month',
  last_month: 'dashboard.last_month',
};

function KpiSkeleton() {
  return (
    <div
      style={{
        height: '120px',
        background: '#e9ecef',
        borderRadius: '4px',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function formatMinutes(seconds: number): string {
  return `${(seconds / 60).toFixed(1)} min`;
}

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

export function Dashboard() {
  const { t } = useTranslation();
  const { token, isGlobalAdmin } = useAuth();
  const [period, setPeriod] = useState<Period>('7days');
  const [interval, setInterval] = useState<Interval>('day');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tenantScope, setTenantScope] = useState<'current' | 'all'>('current');

  const { metrics, timeSeries, isLoading, trend, refetch: metricsRefetch } =
    useDashboardMetrics(period, interval, tenantScope);

  // 60-second auto-refresh
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      metricsRefetch();
    }, 60000);
    return () => clearInterval(id);
  }, [token, metricsRefetch]);

  const handleRefresh = useCallback(() => {
    setLastUpdated(new Date());
    metricsRefetch();
  }, [metricsRefetch]);

  useEffect(() => {
    if (!isLoading) {
      setLastUpdated((prev) => prev ?? new Date());
    }
  }, [isLoading]);

  return (
    <AppWrapper>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h1 className="mb-0">{t('dashboard.title')}</h1>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
            >
              {PERIOD_VALUES.map((value) => (
                <option key={value} value={value}>
                  {t(periodLabelKeys[value])}
                </option>
              ))}
            </select>
            {isGlobalAdmin && (
              <div
                className="btn-group"
                role="group"
                aria-label={t('dashboard.tenant_scope')}
                data-testid="dashboard-tenant-scope"
              >
                <button
                  type="button"
                  className={`btn btn-sm ${tenantScope === 'current' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTenantScope('current')}
                  aria-pressed={tenantScope === 'current'}
                >
                  {t('dashboard.current_tenant')}
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${tenantScope === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTenantScope('all')}
                  aria-pressed={tenantScope === 'all'}
                >
                  {t('dashboard.all_tenants')}
                </button>
              </div>
            )}
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleRefresh}
              title={t('common.refresh', 'Refresh')}
            >
              <i className="bi bi-arrow-clockwise" />
            </button>
            {lastUpdated && (
              <small className="text-muted">
                {t('dashboard.updated')}: {lastUpdated.toLocaleTimeString()}
              </small>
            )}
          </div>
        </div>

        {/* KPI Cards — row 1 */}
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            {isLoading && !metrics ? (
              <KpiSkeleton />
            ) : (
              <KpiCard
                title={t('dashboard.processed_calls')}
                value={metrics?.processedCalls?.value ?? 0}
                trend={
                  trend.callsDirection && trend.callsPercent !== null
                    ? { value: Math.abs(trend.callsPercent), direction: trend.callsDirection }
                    : undefined
                }
                average={`${(metrics?.processedCalls?.averageDuration ?? 0).toFixed(1)}s avg`}
                period={period}
                icon="bi-telephone"
                largeIcon="bi-telephone-fill"
                color="primary"
              />
            )}
          </div>
          <div className="col-md-3">
            {isLoading && !metrics ? (
              <KpiSkeleton />
            ) : (
              <KpiCard
                title={t('dashboard.tools_run')}
                value={metrics?.toolsRun?.value ?? 0}
                trend={
                  trend.toolsDirection && trend.toolsPercent !== null
                    ? { value: Math.abs(trend.toolsPercent), direction: trend.toolsDirection }
                    : undefined
                }
                average={`${(metrics?.toolsRun?.averageProcessingTime ?? 0).toFixed(0)}ms avg`}
                period={period}
                icon="bi-tools"
                largeIcon="bi-tools"
                color="success"
              />
            )}
          </div>
          <div className="col-md-3">
            {isLoading && !metrics ? (
              <KpiSkeleton />
            ) : (
              <KpiCard
                title={t('dashboard.total_call_time')}
                value={formatMinutes(metrics?.processedCalls?.totalDuration ?? 0)}
                period={period}
                icon="bi-clock"
                largeIcon="bi-clock-fill"
                color="info"
              />
            )}
          </div>
          <div className="col-md-3">
            {isLoading && !metrics ? (
              <KpiSkeleton />
            ) : (
              <KpiCard
                title={t('dashboard.tool_processing_time')}
                value={formatSeconds(metrics?.toolsRun?.totalProcessingTime ?? 0)}
                period={period}
                icon="bi-cpu"
                largeIcon="bi-cpu"
                color="warning"
              />
            )}
          </div>
        </div>

        {/* System Health + Chart — row 2 */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <SystemHealthCard />
          </div>
          <div className="col-md-6">
            <Card title={t('dashboard.time_series')} collapsible>
              <TimeSeriesChart
                data={timeSeries}
                interval={interval}
                onIntervalChange={setInterval}
                isLoading={isLoading}
              />
            </Card>
          </div>
        </div>

        {/* Tables — row 3 */}
        <div className="row g-3">
          <div className="col-lg-6">
            <RecentCallsTable />
          </div>
          <div className="col-lg-6">
            <RecentToolLogsTable />
          </div>
        </div>
      </div>
    </AppWrapper>
  );
}
